import { FlyoutListMenuWidget, MenuItemInfo } from '@tableau/widgets';
import * as React from 'react';

import { telemetryConsumerWrapper } from '../../../app/contexts/TelemetryConsumerWrapper';
import { ActionMenuItemClickedTelemetryContext } from '../../../app/contexts/TelemetryContexts';
import { ActionMenuItemClickedEventHandler } from '../../telemetryEvents/types/ContentTelemetryTypes';
import { CommonStyles } from '../CommonStyles';
import { ButtonControlledChildComponentProps } from './ButtonControlledComponent';

/**
 * These props are passed in by the component that instantiates the ButtonControlledComponent
 */
export interface ButtonControlledFlyoutListMenuOwnProps {
  readonly onComponentDidMount: () => void;
  readonly menuItems: MenuItemInfo[];
  readonly onMenuItemSelected: (menuItemValue: string) => void;
}

export type ButtonControlledFlyoutListMenuProps =
    ButtonControlledChildComponentProps &
    ButtonControlledFlyoutListMenuOwnProps;

/**
 * This is a wrapper around the FlyoutListMenuWidget that is meant to be used as the child component
 * of a ButtonControlledComponent, so we can have a menu whose visibility is controlled by a button.
 *
 * To use it, create a wrapper component that takes ButtonControlledChildComponentProps and merges them with
 * ButtonControlledFlyoutListMenuOwnProps, then passes all those props to ButtonControlledFlyoutListMenu.
 *
 * This wrapper takes care of positioning the menu adjacent to the button and closing the menu
 * in the appropriate situations, so that the instantiating component needs to worry only about passing
 * in the menu and the callback handler.
 */
export class ButtonControlledFlyoutListMenu
    extends React.PureComponent<ButtonControlledFlyoutListMenuProps> {

  public componentDidMount() {
    if (this.props.onComponentDidMount) {
      this.props.onComponentDidMount();
    }
  }

  public render(): JSX.Element {
    return telemetryConsumerWrapper(
      ActionMenuItemClickedTelemetryContext.Consumer,
      this.renderFlyoutListMenu
    );
  }

  private renderFlyoutListMenu = (sendActionMenuItemClickTelemetry: ActionMenuItemClickedEventHandler) => {
    return (
      <FlyoutListMenuWidget
        allowAutoPosition={ true }
        menuStyle={{fontWeight: 'normal', top: '-1px'}}
        anchorEl={ this.props.anchorElement }
        handleScrollOnScrollingRoot={ this.props.closeMe }
        menuItemInfos={ this.props.menuItems }
        onSelect={ (menuItemValue: string) => {
          sendActionMenuItemClickTelemetry(menuItemValue);
          this.onMenuItemSelected(menuItemValue);
        } }
        requestCloseAll={ this.props.closeMe }
        testId={ this.props.testId }
        toggleEl={ this.props.anchorElement }
        topFromOverlaidPosition={-1}
        shouldReturnFocusToToggleEl={false} />
    );
  }

  private onMenuItemSelected = (menuItemValue: string): void => {
    this.props.closeMe();
    this.props.onMenuItemSelected(menuItemValue);
  }
}
